import React, { useState, useEffect, useRef } from 'react';
import {
  X, Truck, User, Clock, Coffee, Trash2, Plus,
  CheckCircle2, AlertCircle, Calendar, DollarSign,
  MapPin, Check, Save
} from 'lucide-react';

function toTimeString(val) {
  if (!val) return '';
  const s = String(val).trim();
  if (s.includes('T')) {
    try {
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      }
    } catch (e) {}
  }
  if (s.includes(':')) {
    return s.slice(0, 5);
  }
  return s;
}

function formatSecToMinText(sec = 0) {
  const m = Math.round((sec || 0) / 60);
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h === 0) return `${remM}m`;
  return `${h}h ${remM > 0 ? `${remM}m` : ''}`.trim();
}

export default function JobCardModal({
  job,
  isOpen,
  onClose,
  onSaveJob,
  drivers = [],
  trucks = [],
  hubs = [],
  stops = [],
  jobPauses = []
}) {
  if (!isOpen || !job) return null;

  const [formData, setFormData] = useState({
    id: job.id,
    display_name: job.display_name || '',
    round_id: job.round_id || '',
    driver_id: job.driver_id || '',
    truck_id: job.truck_id || '',
    start_hub_id: job.start_hub_id || '',
    end_hub_id: job.end_hub_id || '',
    status: job.status || 'planned',
    estimated_departure: toTimeString(job.estimated_departure || '07:30'),
    real_departure: toTimeString(job.real_departure),
    estimated_arrival: toTimeString(job.estimated_arrival || '17:00'),
    real_arrival: toTimeString(job.real_arrival || job.real_end),
    estimated_duration_min: Math.round((job.estimated_duration_sec || 0) / 60),
    real_duration_min: Math.round((job.real_duration_sec || 0) / 60),
    estimated_length_km: job.estimated_length_km ?? 0,
    real_length_km: job.real_length_km ?? 0,
    real_start_km: job.real_start_km ?? '',
    real_end_km: job.real_end_km ?? '',
    estimated_cost: job.estimated_cost ?? 0,
    real_cost: job.real_cost ?? 0,
    job_comments: job.job_comments || '',
    is_optimized: job.is_optimized || false
  });

  const [pauses, setPauses] = useState([]);
  const [newPause, setNewPause] = useState({
    type: 'estimated',
    start: '12:00',
    end: '12:30',
    duration_mins: 30,
    pause_label: ''
  });

  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const debounceTimerRef = useRef(null);
  const hasChangesRef = useRef(false);

  // Sync initial state when job changes
  useEffect(() => {
    if (job) {
      setFormData({
        id: job.id,
        display_name: job.display_name || '',
        round_id: job.round_id || '',
        driver_id: job.driver_id || '',
        truck_id: job.truck_id || '',
        start_hub_id: job.start_hub_id || '',
        end_hub_id: job.end_hub_id || '',
        status: job.status || 'planned',
        estimated_departure: toTimeString(job.estimated_departure || '07:30'),
        real_departure: toTimeString(job.real_departure),
        estimated_arrival: toTimeString(job.estimated_arrival || '17:00'),
        real_arrival: toTimeString(job.real_arrival || job.real_end),
        estimated_duration_min: Math.round((job.estimated_duration_sec || 0) / 60),
        real_duration_min: Math.round((job.real_duration_sec || 0) / 60),
        estimated_length_km: job.estimated_length_km ?? 0,
        real_length_km: job.real_length_km ?? 0,
        real_start_km: job.real_start_km ?? '',
        real_end_km: job.real_end_km ?? '',
        estimated_cost: job.estimated_cost ?? 0,
        real_cost: job.real_cost ?? 0,
        job_comments: job.job_comments || '',
        is_optimized: job.is_optimized || false
      });
      fetchPauses(job.id);
    }
  }, [job]);

  const fetchPauses = async (jobId) => {
    try {
      const res = await fetch('/job-pauses');
      if (res.ok) {
        const allPauses = await res.json();
        setPauses(allPauses.filter(p => parseInt(p.job_id) === parseInt(jobId)));
      } else if (jobPauses && jobPauses.length > 0) {
        setPauses(jobPauses.filter(p => parseInt(p.job_id) === parseInt(jobId)));
      }
    } catch (e) {
      if (jobPauses && jobPauses.length > 0) {
        setPauses(jobPauses.filter(p => parseInt(p.job_id) === parseInt(jobId)));
      }
    }
  };

  // Build backend payload from local formData
  const buildPayload = (data) => {
    const payload = {
      display_name: data.display_name,
      round_id: data.round_id ? parseInt(data.round_id) : null,
      driver_id: data.driver_id ? parseInt(data.driver_id) : null,
      truck_id: data.truck_id ? parseInt(data.truck_id) : null,
      start_hub_id: data.start_hub_id ? parseInt(data.start_hub_id) : null,
      end_hub_id: data.end_hub_id ? parseInt(data.end_hub_id) : null,
      status: data.status,
      estimated_departure: data.estimated_departure || null,
      estimated_arrival: data.estimated_arrival || null,
      real_start_km: data.real_start_km !== '' ? parseFloat(data.real_start_km) : null,
      real_end_km: data.real_end_km !== '' ? parseFloat(data.real_end_km) : null,
      estimated_length_km: parseFloat(data.estimated_length_km) || 0,
      real_length_km: parseFloat(data.real_length_km) || 0,
      estimated_cost: parseFloat(data.estimated_cost) || 0,
      real_cost: parseFloat(data.real_cost) || 0,
      estimated_duration_sec: parseInt(data.estimated_duration_min || 0) * 60,
      real_duration_sec: parseInt(data.real_duration_min || 0) * 60,
      job_comments: data.job_comments
    };

    // Format timestamps for real departure / arrival
    const today = new Date().toISOString().slice(0, 10);
    if (data.real_departure) {
      payload.real_departure = `${today}T${data.real_departure}:00`;
    } else {
      payload.real_departure = null;
    }

    if (data.real_arrival) {
      payload.real_arrival = `${today}T${data.real_arrival}:00`;
      payload.real_end = `${today}T${data.real_arrival}:00`;
    } else {
      payload.real_arrival = null;
      payload.real_end = null;
    }

    return payload;
  };

  // Perform in-place API save
  const saveInPlace = async (updatedData) => {
    setSaveStatus('saving');
    setErrorMessage('');
    try {
      const payload = buildPayload(updatedData);
      const res = await fetch(`/api/jobs/${job.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details || err.error || 'Erreur de sauvegarde');
      }
      setSaveStatus('saved');
      hasChangesRef.current = true;
      setTimeout(() => setSaveStatus('idle'), 2200);
    } catch (err) {
      console.error('Error auto-saving job:', err);
      setSaveStatus('error');
      setErrorMessage(err.message || 'Échec de sauvegarde');
    }
  };

  // Debounced trigger for text/numeric inputs
  const triggerSave = (updatedData) => {
    setSaveStatus('saving');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      saveInPlace(updatedData);
    }, 500);
  };

  const handleFieldChange = (field, value, isDirect = false) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };

      // Auto-compute real duration if both real times exist
      if ((field === 'real_departure' || field === 'real_arrival') && next.real_departure && next.real_arrival) {
        const [dh, dm] = next.real_departure.split(':').map(Number);
        const [ah, am] = next.real_arrival.split(':').map(Number);
        const diffMins = (ah * 60 + am) - (dh * 60 + dm);
        if (diffMins >= 0) {
          next.real_duration_min = diffMins;
        }
      }

      // Auto-compute real length km if both km indexes exist
      if ((field === 'real_start_km' || field === 'real_end_km') && next.real_start_km !== '' && next.real_end_km !== '') {
        const diffKm = parseFloat(next.real_end_km) - parseFloat(next.real_start_km);
        if (!isNaN(diffKm) && diffKm >= 0) {
          next.real_length_km = Math.round(diffKm * 100) / 100;
        }
      }

      if (isDirect) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        saveInPlace(next);
      } else {
        triggerSave(next);
      }
      return next;
    });
  };

  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    saveInPlace(formData);
  };

  // Pause CRUD
  const handleAddPause = async () => {
    if (!newPause.duration_mins || newPause.duration_mins <= 0) return;
    try {
      const payload = {
        job_id: job.id,
        type: newPause.type,
        start: newPause.start || null,
        end: newPause.end || null,
        duration_mins: parseInt(newPause.duration_mins),
        pause_label: newPause.pause_label || (newPause.type === 'real' ? 'Pause réelle' : 'Pause planifiée')
      };
      const res = await fetch('/job-pauses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const created = await res.json();
        setPauses(prev => [...prev, created]);
        setNewPause({
          type: 'estimated',
          start: '12:00',
          end: '12:30',
          duration_mins: 30,
          pause_label: ''
        });
        hasChangesRef.current = true;
      }
    } catch (e) {
      console.error('Failed to create pause:', e);
    }
  };

  const handleDeletePause = async (pauseId) => {
    try {
      const res = await fetch(`/job-pauses/${pauseId}`, { method: 'DELETE' });
      if (res.ok) {
        setPauses(prev => prev.filter(p => p.id !== pauseId));
        hasChangesRef.current = true;
      }
    } catch (e) {
      console.error('Failed to delete pause:', e);
    }
  };

  const handleUpdatePauseField = async (pauseId, field, val) => {
    const updatedPauses = pauses.map(p => {
      if (p.id === pauseId) return { ...p, [field]: val };
      return p;
    });
    setPauses(updatedPauses);

    const targetPause = updatedPauses.find(p => p.id === pauseId);
    if (!targetPause) return;

    try {
      await fetch(`/job-pauses/${pauseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration_mins: targetPause.duration_mins,
          pause_label: targetPause.pause_label,
          start: targetPause.start,
          end: targetPause.end,
          type: targetPause.type
        })
      });
      hasChangesRef.current = true;
    } catch (e) {
      console.error('Failed to update pause:', e);
    }
  };

  // Delete Job
  const handleDeleteJob = async () => {
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/jobs/${job.id}`, { method: 'DELETE' });
      if (res.ok) {
        if (onClose) onClose();
      }
    } catch (e) {
      console.error('Failed to delete job:', e);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleClose = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (onClose) onClose();
  };

  const jobStops = stops.filter(s => parseInt(s.job_id) === parseInt(job.id));
  const totalWeight = jobStops.reduce((sum, s) => sum + (parseFloat(s.weight_kg) || 0), 0);
  const totalVolume = jobStops.reduce((sum, s) => sum + (parseFloat(s.volume_m3) || 0), 0);
  const totalRevenue = jobStops.reduce((sum, s) => sum + (parseFloat(s.revenue) || 0), 0);

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div
        className="overlay-card"
        style={{
          width: '880px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          overflow: 'hidden',
          borderRadius: '12px',
          background: '#0f172a',
          border: '1px solid #334155',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)'
        }}
      >
        {/* HEADER BAR */}
        <div
          style={{
            background: '#1e293b',
            padding: '12px 18px',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            <span
              style={{
                background: '#38bdf8',
                color: '#0f172a',
                padding: '2px 8px',
                borderRadius: '6px',
                fontWeight: '900',
                fontSize: '0.8rem',
                letterSpacing: '0.5px'
              }}
            >
              #{formData.id}
            </span>

            <h2
              style={{
                fontSize: '1.05rem',
                fontWeight: '800',
                color: '#f8fafc',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {formData.display_name || `Mission #${formData.id}`}
            </h2>

            {formData.is_optimized && (
              <span
                style={{
                  background: 'rgba(34, 197, 94, 0.15)',
                  color: '#22c55e',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  padding: '2px 7px',
                  borderRadius: '4px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <CheckCircle2 size={12} /> OPTIMISÉE
              </span>
            )}
          </div>

          {/* SAVE STATUS INDICATOR */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {saveStatus === 'saving' && (
              <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span className="spinner-border" style={{ width: '12px', height: '12px', border: '2px solid #38bdf8', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
                Enregistrement...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Check size={14} /> Enregistré
              </span>
            )}
            {saveStatus === 'error' && (
              <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertCircle size={14} /> {errorMessage || 'Erreur'}
              </span>
            )}

            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Fermer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* MODAL BODY (DENSE UNIFIED GRID) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px', color: '#f8fafc' }}>
          
          {/* SECTION 1: AFFECTATION & STATUT */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Nom de mission</label>
              <input
                type="text"
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.85rem', fontWeight: '600' }}
                value={formData.display_name}
                onChange={e => handleFieldChange('display_name', e.target.value)}
                onBlur={handleBlur}
                placeholder="ex: Tournée Ouest Prioritaire"
              />
            </div>

            <div>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Statut</label>
              <select
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem', fontWeight: '700' }}
                value={formData.status}
                onChange={e => handleFieldChange('status', e.target.value, true)}
              >
                <option value="planned">Planifiée</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminée</option>
                <option value="canceled">Annulée</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Chauffeur</label>
              <select
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
                value={formData.driver_id}
                onChange={e => handleFieldChange('driver_id', e.target.value, true)}
              >
                <option value="">Non assigné</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name} ({d.driving_license || 'B'})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Véhicule</label>
              <select
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
                value={formData.truck_id}
                onChange={e => handleFieldChange('truck_id', e.target.value, true)}
              >
                <option value="">Non assigné</option>
                {trucks.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.plate})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Dépôt départ</label>
              <select
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
                value={formData.start_hub_id}
                onChange={e => handleFieldChange('start_hub_id', e.target.value, true)}
              >
                <option value="">Par défaut</option>
                {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>Dépôt retour</label>
              <select
                className="search-input"
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
                value={formData.end_hub_id}
                onChange={e => handleFieldChange('end_hub_id', e.target.value, true)}
              >
                <option value="">Par défaut</option>
                {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
          </div>

          {/* HORAIRES & DURÉE (SANS ENCAPSULATION) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Départ est.</label>
              <input
                type="time"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.estimated_departure}
                onChange={e => handleFieldChange('estimated_departure', e.target.value, true)}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#22c55e', display: 'block', marginBottom: '2px', fontWeight: '700' }}>Départ réel</label>
              <input
                type="time"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', borderColor: '#22c55e' }}
                value={formData.real_departure}
                onChange={e => handleFieldChange('real_departure', e.target.value, true)}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Arrivée est.</label>
              <input
                type="time"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.estimated_arrival}
                onChange={e => handleFieldChange('estimated_arrival', e.target.value, true)}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#22c55e', display: 'block', marginBottom: '2px', fontWeight: '700' }}>Arrivée réelle</label>
              <input
                type="time"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', borderColor: '#22c55e' }}
                value={formData.real_arrival}
                onChange={e => handleFieldChange('real_arrival', e.target.value, true)}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Durée est. (min)</label>
              <input
                type="number"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.estimated_duration_min}
                onChange={e => handleFieldChange('estimated_duration_min', e.target.value)}
                onBlur={handleBlur}
              />
              <span style={{ fontSize: '0.68rem', color: '#64748b' }}>{formatSecToMinText(formData.estimated_duration_min * 60)}</span>
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#22c55e', display: 'block', marginBottom: '2px', fontWeight: '700' }}>Durée réelle (min)</label>
              <input
                type="number"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', borderColor: '#22c55e' }}
                value={formData.real_duration_min}
                onChange={e => handleFieldChange('real_duration_min', e.target.value)}
                onBlur={handleBlur}
              />
              <span style={{ fontSize: '0.68rem', color: '#64748b' }}>{formatSecToMinText(formData.real_duration_min * 60)}</span>
            </div>
          </div>

          {/* KILOMÉTRAGE & COÛTS (SANS ENCAPSULATION) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Dist. est. (km)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.estimated_length_km}
                onChange={e => handleFieldChange('estimated_length_km', e.target.value)}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#22c55e', display: 'block', marginBottom: '2px', fontWeight: '700' }}>Dist. réelle (km)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', borderColor: '#22c55e' }}
                value={formData.real_length_km}
                onChange={e => handleFieldChange('real_length_km', e.target.value)}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Index départ</label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.real_start_km}
                onChange={e => handleFieldChange('real_start_km', e.target.value)}
                onBlur={handleBlur}
                placeholder="ex: 12450"
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Index fin</label>
              <input
                type="number"
                step="0.1"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.real_end_km}
                onChange={e => handleFieldChange('real_end_km', e.target.value)}
                onBlur={handleBlur}
                placeholder="ex: 12518"
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Coût est. (CHF)</label>
              <input
                type="number"
                step="0.05"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem' }}
                value={formData.estimated_cost}
                onChange={e => handleFieldChange('estimated_cost', e.target.value)}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.7rem', color: '#22c55e', display: 'block', marginBottom: '2px', fontWeight: '700' }}>Coût réel (CHF)</label>
              <input
                type="number"
                step="0.05"
                min="0"
                className="search-input"
                style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', borderColor: '#22c55e' }}
                value={formData.real_cost}
                onChange={e => handleFieldChange('real_cost', e.target.value)}
                onBlur={handleBlur}
              />
            </div>
          </div>

          {/* PAUSES (SANS ENCAPSULATION) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>
                Pauses ({pauses.length})
              </label>
              <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                Total: <b>{pauses.reduce((acc, p) => acc + parseInt(p.duration_mins || 15), 0)} min</b>
              </span>
            </div>

            {/* PAUSE LIST */}
            {pauses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '140px', overflowY: 'auto' }}>
                {pauses.map(p => {
                  const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                  return (
                    <div
                      key={p.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '85px 1fr 75px 75px 65px 30px',
                        gap: '8px',
                        alignItems: 'center',
                        fontSize: '0.78rem'
                      }}
                    >
                      <select
                        className="search-input"
                        style={{ padding: '3px 6px', fontSize: '0.72rem', fontWeight: '700', color: isReal ? '#22c55e' : '#38bdf8' }}
                        value={p.type || 'estimated'}
                        onChange={e => handleUpdatePauseField(p.id, 'type', e.target.value)}
                      >
                        <option value="estimated">Planifiée</option>
                        <option value="real">Réelle</option>
                      </select>

                      <input
                        type="text"
                        className="search-input"
                        style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                        value={p.pause_label || ''}
                        placeholder="Motif de pause..."
                        onChange={e => handleUpdatePauseField(p.id, 'pause_label', e.target.value)}
                      />

                      <input
                        type="time"
                        className="search-input"
                        style={{ padding: '3px 6px', fontSize: '0.72rem' }}
                        value={toTimeString(p.start)}
                        onChange={e => handleUpdatePauseField(p.id, 'start', e.target.value)}
                      />

                      <input
                        type="time"
                        className="search-input"
                        style={{ padding: '3px 6px', fontSize: '0.72rem' }}
                        value={toTimeString(p.end)}
                        onChange={e => handleUpdatePauseField(p.id, 'end', e.target.value)}
                      />

                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <input
                          type="number"
                          min="1"
                          className="search-input"
                          style={{ width: '45px', padding: '3px 5px', fontSize: '0.72rem', textAlign: 'center' }}
                          value={p.duration_mins || 15}
                          onChange={e => handleUpdatePauseField(p.id, 'duration_mins', e.target.value)}
                        />
                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>m</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeletePause(p.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Supprimer la pause"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: '0.74rem', color: '#64748b', fontStyle: 'italic', padding: '2px 0' }}>
                Aucune pause enregistrée.
              </div>
            )}

            {/* ADD NEW PAUSE INLINE */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '95px 1fr 75px 75px 65px 75px',
                gap: '8px',
                alignItems: 'center',
                marginTop: '2px'
              }}
            >
              <select
                className="search-input"
                style={{ padding: '4px 6px', fontSize: '0.72rem', fontWeight: '700' }}
                value={newPause.type}
                onChange={e => setNewPause({ ...newPause, type: e.target.value })}
              >
                <option value="estimated">+ Planifiée</option>
                <option value="real">+ Réelle</option>
              </select>

              <input
                type="text"
                className="search-input"
                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                placeholder="Libellé (ex: Déjeuner, Repos...)"
                value={newPause.pause_label}
                onChange={e => setNewPause({ ...newPause, pause_label: e.target.value })}
              />

              <input
                type="time"
                className="search-input"
                style={{ padding: '4px 6px', fontSize: '0.72rem' }}
                value={newPause.start}
                onChange={e => setNewPause({ ...newPause, start: e.target.value })}
                title="Heure début"
              />

              <input
                type="time"
                className="search-input"
                style={{ padding: '4px 6px', fontSize: '0.72rem' }}
                value={newPause.end}
                onChange={e => setNewPause({ ...newPause, end: e.target.value })}
                title="Heure fin"
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <input
                  type="number"
                  min="1"
                  className="search-input"
                  style={{ width: '45px', padding: '4px 5px', fontSize: '0.72rem', textAlign: 'center' }}
                  value={newPause.duration_mins}
                  onChange={e => setNewPause({ ...newPause, duration_mins: e.target.value })}
                />
                <span style={{ fontSize: '0.68rem', color: '#64748b' }}>m</span>
              </div>

              <button
                type="button"
                onClick={handleAddPause}
                style={{
                  background: '#38bdf8',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '5px 8px',
                  fontSize: '0.72rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={13} /> Ajouter
              </button>
            </div>
          </div>

          {/* SECTION 5: COMMENTAIRES & REMARQUES */}
          <div>
            <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
              Commentaires de mission
            </label>
            <textarea
              className="search-input"
              style={{ width: '100%', minHeight: '44px', padding: '6px 10px', fontSize: '0.8rem', resize: 'vertical' }}
              value={formData.job_comments}
              onChange={e => handleFieldChange('job_comments', e.target.value)}
              onBlur={handleBlur}
              placeholder="Consignes particulières, directives chauffeur..."
            />
          </div>

          {/* SECTION 6: LIVRAISONS RATTACHÉES */}
          <div style={{ borderTop: '1px solid #334155', paddingTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#f8fafc', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Livraisons rattachées ({jobStops.length})
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                <b>{totalWeight.toFixed(1)} kg</b> | <b>{totalVolume.toFixed(2)} m³</b> | <b style={{ color: '#22c55e' }}>{totalRevenue.toFixed(2)} CHF</b>
              </div>
            </div>

            {jobStops.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '150px', overflowY: 'auto' }}>
                {jobStops.map((s, idx) => (
                  <div
                    key={s.id}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '5px',
                      padding: '5px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.78rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          background: '#38bdf8',
                          color: '#0f172a',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '800',
                          fontSize: '0.68rem'
                        }}
                      >
                        {idx + 1}
                      </span>
                      <strong style={{ color: '#ffffff' }}>{s.client_name}</strong>
                      <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{s.city}</span>
                      {s.estimated_arrival && (
                        <span style={{ color: '#38bdf8', fontSize: '0.7rem' }}>• {toTimeString(s.estimated_arrival)}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.74rem' }}>
                      <span style={{ color: '#cbd5e1' }}>{s.weight_kg || 0} kg</span>
                      <span style={{ color: '#cbd5e1' }}>{s.volume_m3 || 0} m³</span>
                      <span style={{ color: '#22c55e', fontWeight: '700' }}>{s.revenue || 0} CHF</span>
                      <span className={`status-pill ${s.status === 'delivered' ? 'completed' : 'ready'}`} style={{ fontSize: '0.68rem', padding: '1px 6px' }}>
                        {s.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic', padding: '6px 0' }}>
                Aucune livraison n'est actuellement assignée à cette mission.
              </div>
            )}
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div
          style={{
            background: '#1e293b',
            borderTop: '1px solid #334155',
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  background: 'none',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#ef4444',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Trash2 size={13} /> Supprimer la mission
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '700' }}>
                  Confirmer la suppression ?
                </span>
                <button
                  type="button"
                  onClick={handleDeleteJob}
                  disabled={isDeleting}
                  style={{
                    background: '#ef4444',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    fontSize: '0.72rem',
                    fontWeight: '800',
                    cursor: 'pointer'
                  }}
                >
                  {isDeleting ? 'Suppression...' : 'Oui, supprimer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    background: '#334155',
                    color: '#cbd5e1',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    fontSize: '0.72rem',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
              </div>
            )}
          </div>

          <div>
            <button
              type="button"
              className="btn-action"
              onClick={handleClose}
              style={{
                background: '#38bdf8',
                color: '#0f172a',
                border: 'none',
                fontWeight: '800',
                padding: '6px 18px',
                fontSize: '0.82rem',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
